// AdminCategoryManagement.jsx - Updated with full tier editing

import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Badge,
  Table,
} from "react-bootstrap";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaFolder,
  FaTag,
  FaBox,
  FaChevronDown,
  FaChevronRight,
  FaToggleOn,
  FaToggleOff,
  FaImage,
  FaUpload,
  FaTimesCircle,
  FaLayerGroup,
  FaList,
} from "react-icons/fa";

const AdminCategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [apiUrl, setApiUrl] = useState("");

  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubCategoryModal, setShowSubCategoryModal] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showTierModal, setShowTierModal] = useState(false);
  const [showSizeVariantModal, setShowSizeVariantModal] = useState(false);

  // Selected items for edit
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);

  // Image upload state
  const [imageEntity, setImageEntity] = useState({
    type: "",
    categoryId: null,
    subCategoryId: null,
    packageId: null,
    tierId: null,
    currentImageUrl: null,
    name: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Form data
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    icon: "🔧",
    color: "#4f46e5",
    imageUrl: "",
    order: 0,
    discountPercentage: 0,
    isActive: true,
  });
  const [subCategoryForm, setSubCategoryForm] = useState({
    name: "",
    imageUrl: "",
    order: 0,
    isActive: true,
  });
  const [packageForm, setPackageForm] = useState({
    name: "",
    price: "",
    imageUrl: "",
    unit: "per visit",
    isActive: true,
  });
  const [tierForm, setTierForm] = useState({
    name: "",
    isActive: true,
    descriptionPoints: [],
    variants: [], // Store all variants here
  });
  const [variantForm, setVariantForm] = useState({
    type: "",
    bhk: "",
    duration: "1 hr",
    price: "",
  });

  // Expanded sections
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedSubCategories, setExpandedSubCategories] = useState({});
  const [expandedTiers, setExpandedTiers] = useState({});

  // Action loading states
  const [actionLoading, setActionLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  // ── API Configuration ──────────────────────────────────────────────────────
  const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

  useEffect(() => {
    setApiUrl(API_BASE);
    console.log("🔗 API_BASE URL:", API_BASE);
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken") || sessionStorage.getItem("adminToken");
    const headers = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
  };

  // ── Helper to normalize package data ──────────────────────────────────────
  const normalizePackage = (pkg) => {
    let price = pkg.price;
    if (price === undefined || price === null) {
      if (pkg.priceMin !== undefined && pkg.priceMin !== null) {
        price = pkg.priceMin;
      } else if (pkg.priceMax !== undefined && pkg.priceMax !== null) {
        price = pkg.priceMax;
      } else {
        price = 0;
      }
    }
    return {
      ...pkg,
      price: price,
      displayPrice: price,
      isActive: pkg.isActive !== false,
      imageUrl: pkg.imageUrl || null,
      tiers: pkg.tiers || [],
    };
  };

  const normalizeSubCategory = (sub) => {
    const packages = (sub.packages || []).map(pkg => normalizePackage(pkg));
    return {
      ...sub,
      isActive: sub.isActive !== false,
      packages: packages,
      imageUrl: sub.imageUrl || null,
    };
  };

  // ── Fetch all categories ──────────────────────────────────────────────────
  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      const url = `${API_BASE}/api/categories/admin/all`;
      console.log(`📡 Fetching categories from: ${url}`);

      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });

      console.log(`📊 Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log("📦 Raw categories data received:", data);
      
      const rawCategoriesList = data.dev?.categories || data.categories || data.data?.categories || (Array.isArray(data.data) ? data.data : []);
      const normalizedCategories = rawCategoriesList.map(cat => {
        const subCategories = (cat.subCategories || []).map(sub => normalizeSubCategory(sub));
        return {
          ...cat,
          isActive: cat.isActive !== false,
          subCategories: subCategories,
          imageUrl: cat.imageUrl || null,
          icon: cat.icon || "📁",
        };
      });
      
      console.log(`📦 Total categories after normalization: ${normalizedCategories.length}`);
      setCategories(normalizedCategories);
    } catch (err) {
      console.error("❌ Fetch error:", err);
      setError(`Failed to fetch categories: ${err.message}`);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // ── Helper Functions ──────────────────────────────────────────────────────
  const hasTiers = (pkg) => {
    return pkg.tiers && Array.isArray(pkg.tiers) && pkg.tiers.length > 0;
  };

  const getPackageDisplayPrice = (pkg) => {
    if (hasTiers(pkg)) {
      const prices = pkg.tiers.flatMap(t => 
        (t.sizeVariants || []).map(v => v.price)
      );
      if (prices.length === 0) return "N/A";
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      return min === max ? `₹${min}` : `₹${min} - ₹${max}`;
    }
    return pkg.price ? `₹${pkg.price}` : "N/A";
  };

  const getTierDisplayPrice = (tier) => {
    if (tier.sizeVariants && tier.sizeVariants.length > 0) {
      const prices = tier.sizeVariants.map(v => v.price);
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      return min === max ? `₹${min}` : `₹${min} - ₹${max}`;
    }
    return "N/A";
  };

  // ── Image Upload Handlers ──────────────────────────────────────────────────
  const openImageModal = (type, category, subCategory = null, pkg = null, tier = null) => {
    let entityData = {
      type,
      categoryId: category.id,
      currentImageUrl: null,
      name: category.name,
    };

    if (type === "subcategory" && subCategory) {
      entityData = {
        ...entityData,
        subCategoryId: subCategory.id,
        currentImageUrl: subCategory.imageUrl || null,
        name: subCategory.name,
      };
    } else if (type === "package" && subCategory && pkg) {
      entityData = {
        ...entityData,
        subCategoryId: subCategory.id,
        packageId: pkg.id,
        currentImageUrl: pkg.imageUrl || null,
        name: pkg.name,
      };
    } else if (type === "tier" && subCategory && pkg && tier) {
      entityData = {
        ...entityData,
        subCategoryId: subCategory.id,
        packageId: pkg.id,
        tierId: tier.id,
        currentImageUrl: tier.imageUrl || null,
        name: `${pkg.name} - ${tier.name}`,
      };
    }

    setImageEntity(entityData);
    setImageFile(null);
    setImagePreview(null);
    setShowImageModal(true);
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async () => {
    if (!imageFile) {
      setError("Please select an image to upload");
      return;
    }

    setImageUploading(true);
    const formData = new FormData();
    formData.append("image", imageFile);

    try {
      let endpoint = "";
      const { type, categoryId, subCategoryId, packageId, tierId } = imageEntity;

      switch (type) {
        case "category":
          endpoint = `${API_BASE}/api/categories/${categoryId}/image/upload`;
          break;
        case "subcategory":
          endpoint = `${API_BASE}/api/categories/${categoryId}/subcategories/${subCategoryId}/image/upload`;
          break;
        case "package":
          endpoint = `${API_BASE}/api/categories/${categoryId}/subcategories/${subCategoryId}/packages/${packageId}/image/upload`;
          break;
        case "tier":
          const tierPkgId = `${packageId}_${tierId}`;
          endpoint = `${API_BASE}/api/categories/${categoryId}/subcategories/${subCategoryId}/packages/${tierPkgId}/image/upload`;
          break;
        default:
          throw new Error("Invalid entity type");
      }

      console.log(`📡 Uploading image to: ${endpoint}`);

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: getAuthHeaders().Authorization || "",
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to upload image");
      }

      await fetchCategories();
      setShowImageModal(false);
      setImageFile(null);
      setImagePreview(null);
      setSuccess("Image uploaded successfully");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setImageUploading(false);
    }
  };

  const handleImageDelete = async () => {
    if (!window.confirm("Are you sure you want to remove this image?")) return;

    setImageUploading(true);
    try {
      const { type, categoryId, subCategoryId, packageId, tierId } = imageEntity;
      let endpoint = "";

      switch (type) {
        case "category":
          endpoint = `${API_BASE}/api/categories/${categoryId}/image`;
          break;
        case "subcategory":
          endpoint = `${API_BASE}/api/categories/${categoryId}/subcategories/${subCategoryId}/image`;
          break;
        case "package":
          endpoint = `${API_BASE}/api/categories/${categoryId}/subcategories/${subCategoryId}/packages/${packageId}/image`;
          break;
        case "tier":
          const tierPkgId = `${packageId}_${tierId}`;
          endpoint = `${API_BASE}/api/categories/${categoryId}/subcategories/${subCategoryId}/packages/${tierPkgId}/image`;
          break;
        default:
          throw new Error("Invalid entity type");
      }

      const response = await fetch(endpoint, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to delete image");
      }

      await fetchCategories();
      setShowImageModal(false);
      setImageFile(null);
      setImagePreview(null);
      setSuccess("Image removed successfully");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setImageUploading(false);
    }
  };

  const sanitizePayload = (formData) => {
    const payload = { ...formData };
    Object.keys(payload).forEach((key) => {
      if (typeof payload[key] === "string") {
        const trimmed = payload[key].trim();
        if (key === "imageUrl" && !trimmed) {
          delete payload[key];
        } else {
          payload[key] = trimmed;
        }
      }
    });
    return payload;
  };

  // ── Category CRUD ──────────────────────────────────────────────────────────
  const handleCreateCategory = async () => {
    if (!categoryForm.name.trim()) {
      setError("Category name is required");
      return;
    }
    setActionLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/categories`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(sanitizePayload(categoryForm)),
      });
      if (!response.ok) {
        const errorData = await response.json();
        const detailMsg = Array.isArray(errorData.details)
          ? errorData.details.map((d) => `${d.path || d.param}: ${d.msg}`).join(", ")
          : null;
        throw new Error(detailMsg || errorData.error || "Failed to create category");
      }
      await fetchCategories();
      setShowCategoryModal(false);
      resetCategoryForm();
      setSuccess("Category created successfully");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateCategory = async () => {
    if (!selectedCategory) return;
    setActionLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/categories/${selectedCategory.id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(sanitizePayload(categoryForm)),
      });
      if (!response.ok) {
        const errorData = await response.json();
        const detailMsg = Array.isArray(errorData.details)
          ? errorData.details.map((d) => `${d.path || d.param}: ${d.msg}`).join(", ")
          : null;
        throw new Error(detailMsg || errorData.error || "Failed to update category");
      }
      await fetchCategories();
      setShowCategoryModal(false);
      setSelectedCategory(null);
      resetCategoryForm();
      setSuccess("Category updated successfully");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm(`Delete "${category.name}"? This will also delete all sub-categories and packages.`)) return;
    setActionLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/categories/${category.id}?hard=true`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error("Failed to delete category");
      await fetchCategories();
      setSuccess(`Category "${category.name}" deleted`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleCategoryStatus = async (category) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/categories/${category.id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ isActive: !category.isActive }),
      });
      if (!response.ok) throw new Error("Failed to update status");
      await fetchCategories();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // ── Sub-Category CRUD ──────────────────────────────────────────────────────
  const handleCreateSubCategory = async () => {
    if (!selectedCategory || !subCategoryForm.name.trim()) {
      setError("Sub-category name is required");
      return;
    }
    setActionLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/categories/${selectedCategory.id}/subcategories`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify(sanitizePayload(subCategoryForm)),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        const detailMsg = Array.isArray(errorData.details)
          ? errorData.details.map((d) => `${d.path || d.param}: ${d.msg}`).join(", ")
          : null;
        throw new Error(detailMsg || errorData.error || "Failed to create sub-category");
      }
      await fetchCategories();
      setShowSubCategoryModal(false);
      resetSubCategoryForm();
      setSuccess("Sub-category created successfully");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateSubCategory = async () => {
    if (!selectedCategory || !selectedSubCategory) return;
    setActionLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/categories/${selectedCategory.id}/subcategories/${selectedSubCategory.id}`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify(sanitizePayload(subCategoryForm)),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        const detailMsg = Array.isArray(errorData.details)
          ? errorData.details.map((d) => `${d.path || d.param}: ${d.msg}`).join(", ")
          : null;
        throw new Error(detailMsg || errorData.error || "Failed to update sub-category");
      }
      await fetchCategories();
      setShowSubCategoryModal(false);
      setSelectedSubCategory(null);
      resetSubCategoryForm();
      setSuccess("Sub-category updated successfully");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteSubCategory = async (category, subCategory) => {
    if (!window.confirm(`Delete "${subCategory.name}"? This will also delete all packages under it.`)) return;
    setActionLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/categories/${category.id}/subcategories/${subCategory.id}?hard=true`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        }
      );
      if (!response.ok) throw new Error("Failed to delete sub-category");
      await fetchCategories();
      setSuccess(`Sub-category "${subCategory.name}" deleted`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // ── Package CRUD ────────────────────────────────────────────────────────────
  const handleCreatePackage = async () => {
    if (!selectedCategory || !selectedSubCategory || !packageForm.name.trim()) {
      setError("Package name is required");
      return;
    }
    if (!packageForm.price) {
      setError("Price is required");
      return;
    }

    const priceNum = parseFloat(packageForm.price);
    if (isNaN(priceNum) || priceNum < 0) {
      setError("Valid price is required (positive number)");
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/categories/${selectedCategory.id}/subcategories/${selectedSubCategory.id}/packages`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            name: packageForm.name,
            priceMin: priceNum,
            priceMax: priceNum,
            unit: packageForm.unit,
            imageUrl: packageForm.imageUrl || null,
            isActive: packageForm.isActive,
          }),
        }
      );
      if (!response.ok) throw new Error("Failed to create package");
      await fetchCategories();
      setShowPackageModal(false);
      resetPackageForm();
      setSuccess("Package created successfully");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdatePackage = async () => {
    if (!selectedCategory || !selectedSubCategory || !selectedPackage) return;

    const priceNum = parseFloat(packageForm.price);
    if (isNaN(priceNum) || priceNum < 0) {
      setError("Valid price is required (positive number)");
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/categories/${selectedCategory.id}/subcategories/${selectedSubCategory.id}/packages/${selectedPackage.id}`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            name: packageForm.name,
            priceMin: priceNum,
            priceMax: priceNum,
            unit: packageForm.unit,
            imageUrl: packageForm.imageUrl || null,
            isActive: packageForm.isActive,
          }),
        }
      );
      if (!response.ok) throw new Error("Failed to update package");
      await fetchCategories();
      setShowPackageModal(false);
      setSelectedPackage(null);
      resetPackageForm();
      setSuccess("Package updated successfully");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePackage = async (category, subCategory, pkg) => {
    if (!window.confirm(`Delete package "${pkg.name}"?`)) return;
    setActionLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/categories/${category.id}/subcategories/${subCategory.id}/packages/${pkg.id}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        }
      );
      if (!response.ok) throw new Error("Failed to delete package");
      await fetchCategories();
      setSuccess(`Package "${pkg.name}" deleted`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // ── Tier CRUD ──────────────────────────────────────────────────────────────
  const handleCreateTier = async () => {
    if (!selectedCategory || !selectedSubCategory || !selectedPackage) {
      setError("Please select a package first");
      return;
    }
    if (!tierForm.name.trim()) {
      setError("Tier name is required");
      return;
    }

    setActionLoading(true);
    try {
      // Use the first variant's price or default
      const defaultPrice = tierForm.variants.length > 0 ? tierForm.variants[0].price : 0;
      
      const response = await fetch(
        `${API_BASE}/api/categories/${selectedCategory.id}/subcategories/${selectedSubCategory.id}/packages/${selectedPackage.id}/tiers`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            name: tierForm.name,
            price: parseFloat(defaultPrice) || 0,
            isActive: tierForm.isActive,
            descriptionPoints: tierForm.descriptionPoints || [],
            features: {},
            addOns: [],
            sizeVariants: tierForm.variants.length > 0 ? tierForm.variants : [
              {
                type: "Standard",
                duration: "1 hr",
                price: 0,
              },
            ],
          }),
        }
      );
      if (!response.ok) throw new Error("Failed to create tier");
      await fetchCategories();
      setShowTierModal(false);
      resetTierForm();
      setSuccess("Tier created successfully");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateTier = async () => {
    if (!selectedCategory || !selectedSubCategory || !selectedPackage || !selectedTier) return;
    setActionLoading(true);
    try {
      // First update tier basic info
      const response = await fetch(
        `${API_BASE}/api/categories/${selectedCategory.id}/subcategories/${selectedSubCategory.id}/packages/${selectedPackage.id}/tiers/${selectedTier.id}`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            name: tierForm.name,
            isActive: tierForm.isActive,
          }),
        }
      );
      if (!response.ok) throw new Error("Failed to update tier");
      
      // Then update each variant
      for (const variant of tierForm.variants) {
        if (variant.id) {
          // Update existing variant
          await fetch(
            `${API_BASE}/api/categories/${selectedCategory.id}/subcategories/${selectedSubCategory.id}/packages/${selectedPackage.id}/tiers/${selectedTier.id}/variants/${variant.id}`,
            {
              method: "PUT",
              headers: getAuthHeaders(),
              body: JSON.stringify({
                duration: variant.duration,
                price: parseFloat(variant.price),
              }),
            }
          );
        } else {
          // Create new variant
          await fetch(
            `${API_BASE}/api/categories/${selectedCategory.id}/subcategories/${selectedSubCategory.id}/packages/${selectedPackage.id}/tiers/${selectedTier.id}/variants`,
            {
              method: "POST",
              headers: getAuthHeaders(),
              body: JSON.stringify({
                type: variant.type || variant.bhk,
                bhk: variant.bhk || variant.type,
                duration: variant.duration,
                price: parseFloat(variant.price),
              }),
            }
          );
        }
      }
      
      await fetchCategories();
      setShowTierModal(false);
      setSelectedTier(null);
      resetTierForm();
      setSuccess("Tier updated successfully");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteTier = async (category, subCategory, pkg, tier) => {
    if (!window.confirm(`Delete tier "${tier.name}"?`)) return;
    setActionLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/categories/${category.id}/subcategories/${subCategory.id}/packages/${pkg.id}/tiers/${tier.id}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        }
      );
      if (!response.ok) throw new Error("Failed to delete tier");
      await fetchCategories();
      setSuccess(`Tier "${tier.name}" deleted`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // ── Size Variant CRUD (for individual variant management) ──────────────────
  const handleCreateVariant = async () => {
    if (!selectedCategory || !selectedSubCategory || !selectedPackage || !selectedTier) {
      setError("Please select a tier first");
      return;
    }
    if (!variantForm.type && !variantForm.bhk) {
      setError("Variant type or BHK is required");
      return;
    }
    if (!variantForm.price) {
      setError("Price is required");
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/categories/${selectedCategory.id}/subcategories/${selectedSubCategory.id}/packages/${selectedPackage.id}/tiers/${selectedTier.id}/variants`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            type: variantForm.type || variantForm.bhk,
            bhk: variantForm.bhk || variantForm.type,
            duration: variantForm.duration,
            price: parseFloat(variantForm.price),
          }),
        }
      );
      if (!response.ok) throw new Error("Failed to create variant");
      await fetchCategories();
      setShowSizeVariantModal(false);
      resetVariantForm();
      setSuccess("Size variant created successfully");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateVariant = async () => {
    if (!selectedCategory || !selectedSubCategory || !selectedPackage || !selectedTier || !selectedVariant) return;
    setActionLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/categories/${selectedCategory.id}/subcategories/${selectedSubCategory.id}/packages/${selectedPackage.id}/tiers/${selectedTier.id}/variants/${selectedVariant.id || selectedVariant.type}`,
        {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            duration: variantForm.duration,
            price: parseFloat(variantForm.price),
          }),
        }
      );
      if (!response.ok) throw new Error("Failed to update variant");
      await fetchCategories();
      setShowSizeVariantModal(false);
      setSelectedVariant(null);
      resetVariantForm();
      setSuccess("Variant updated successfully");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteVariant = async (category, subCategory, pkg, tier, variant) => {
    if (!window.confirm(`Delete variant "${variant.type || variant.bhk}"?`)) return;
    setActionLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/categories/${category.id}/subcategories/${subCategory.id}/packages/${pkg.id}/tiers/${tier.id}/variants/${variant.id || variant.type}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        }
      );
      if (!response.ok) throw new Error("Failed to delete variant");
      await fetchCategories();
      setSuccess(`Variant "${variant.type || variant.bhk}" deleted`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // ── Form helpers ────────────────────────────────────────────────────────────
  const resetCategoryForm = () => {
    setCategoryForm({ name: "", icon: "🔧", color: "#4f46e5", imageUrl: "", order: 0, discountPercentage: 0, isActive: true });
    setSelectedCategory(null);
  };

  const resetSubCategoryForm = () => {
    setSubCategoryForm({ name: "", imageUrl: "", order: 0, isActive: true });
    setSelectedSubCategory(null);
  };

  const resetPackageForm = () => {
    setPackageForm({ name: "", price: "", imageUrl: "", unit: "per visit", isActive: true });
    setSelectedPackage(null);
  };

  const resetTierForm = () => {
    setTierForm({ name: "", isActive: true, descriptionPoints: [], variants: [] });
    setSelectedTier(null);
  };

  const resetVariantForm = () => {
    setVariantForm({ type: "", bhk: "", duration: "1 hr", price: "" });
    setSelectedVariant(null);
  };

  const openCategoryModal = (category = null) => {
    if (category) {
      setSelectedCategory(category);
      setCategoryForm({
        name: category.name,
        icon: category.icon || "🔧",
        color: category.color || "#4f46e5",
        imageUrl: category.imageUrl || "",
        order: category.order || 0,
        discountPercentage: category.discountPercentage || 0,
        isActive: category.isActive !== false,
      });
    } else {
      resetCategoryForm();
    }
    setShowCategoryModal(true);
  };

  const openSubCategoryModal = (category, subCategory = null) => {
    setSelectedCategory(category);
    if (subCategory) {
      setSelectedSubCategory(subCategory);
      setSubCategoryForm({
        name: subCategory.name,
        imageUrl: subCategory.imageUrl || "",
        order: subCategory.order || 0,
        isActive: subCategory.isActive !== false,
      });
    } else {
      resetSubCategoryForm();
    }
    setShowSubCategoryModal(true);
  };

  const openPackageModal = (category, subCategory, pkg = null) => {
    setSelectedCategory(category);
    setSelectedSubCategory(subCategory);
    if (pkg) {
      setSelectedPackage(pkg);
      setPackageForm({
        name: pkg.name,
        price: pkg.price || "",
        imageUrl: pkg.imageUrl || "",
        unit: pkg.unit || "per visit",
        isActive: pkg.isActive !== false,
      });
    } else {
      resetPackageForm();
    }
    setShowPackageModal(true);
  };

  const openTierModal = (category, subCategory, pkg, tier = null) => {
    setSelectedCategory(category);
    setSelectedSubCategory(subCategory);
    setSelectedPackage(pkg);
    
    if (tier) {
      setSelectedTier(tier);
      // Load all variants into the form
      const variants = (tier.sizeVariants || []).map(v => ({
        id: v.id,
        type: v.type || v.bhk || "",
        bhk: v.bhk || v.type || "",
        duration: v.duration || "1 hr",
        price: v.price || 0,
      }));
      
      setTierForm({
        name: tier.name,
        isActive: tier.isActive !== false,
        descriptionPoints: tier.descriptionPoints || [],
        variants: variants,
      });
    } else {
      resetTierForm();
      // Add a default variant when creating new tier
      setTierForm({
        name: "",
        isActive: true,
        descriptionPoints: [],
        variants: [{ type: "Standard", duration: "1 hr", price: "" }],
      });
    }
    setShowTierModal(true);
  };

  const openVariantModal = (category, subCategory, pkg, tier, variant = null) => {
    setSelectedCategory(category);
    setSelectedSubCategory(subCategory);
    setSelectedPackage(pkg);
    setSelectedTier(tier);
    if (variant) {
      setSelectedVariant(variant);
      setVariantForm({
        type: variant.type || variant.bhk || "",
        bhk: variant.bhk || variant.type || "",
        duration: variant.duration || "1 hr",
        price: variant.price || "",
      });
    } else {
      resetVariantForm();
    }
    setShowSizeVariantModal(true);
  };

  // ── Tier Variant Management ──────────────────────────────────────────────
  const addVariantToTier = () => {
    setTierForm(prev => ({
      ...prev,
      variants: [...prev.variants, { type: "", bhk: "", duration: "1 hr", price: "" }]
    }));
  };

  const removeVariantFromTier = (index) => {
    setTierForm(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const updateVariantInTier = (index, field, value) => {
    setTierForm(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => 
        i === index ? { ...v, [field]: value } : v
      )
    }));
  };

  const toggleCategoryExpand = (categoryId) => {
    setExpandedCategories((prev) => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };

  const toggleSubCategoryExpand = (subCategoryId) => {
    setExpandedSubCategories((prev) => ({ ...prev, [subCategoryId]: !prev[subCategoryId] }));
  };

  const toggleTierExpand = (tierId) => {
    setExpandedTiers((prev) => ({ ...prev, [tierId]: !prev[tierId] }));
  };

  // ── Image component ────────────────────────────────────────────────────────
  const ImageDisplay = ({ imageUrl, alt, size = "50px" }) => {
    if (!imageUrl) {
      return (
        <div
          style={{
            width: size,
            height: size,
            backgroundColor: "#f0f0f0",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px dashed #ccc",
          }}
        >
          <FaImage color="#ccc" size={parseInt(size) * 0.4} />
        </div>
      );
    }
    return (
      <img
        src={imageUrl}
        alt={alt || "Category image"}
        style={{
          width: size,
          height: size,
          objectFit: "cover",
          borderRadius: "8px",
          border: "1px solid #ddd",
        }}
        onError={(e) => {
          e.target.style.display = "none";
        }}
      />
    );
  };

  // ── Render package with tiers ─────────────────────────────────────────────
  const renderPackageWithTiers = (category, subCategory, pkg) => {
    if (!hasTiers(pkg)) {
      // Flat package rendering
      return (
        <div className="border rounded p-2 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <ImageDisplay imageUrl={pkg.imageUrl} alt={pkg.name} size="35px" />
            <div className="ms-2">
              <strong>{pkg.name}</strong>
              <br />
              <small className="text-muted">
                ₹{pkg.price} / {pkg.unit || "visit"}
              </small>
              {pkg.isActive === false && (
                <Badge bg="secondary" className="ms-2">Inactive</Badge>
              )}
            </div>
          </div>
          <div>
            <Button
              variant="outline-success"
              size="sm"
              className="me-1"
              onClick={() => openTierModal(category, subCategory, pkg)}
            >
              <FaPlus /> Tier
            </Button>
            <Button
              variant="outline-info"
              size="sm"
              className="me-1"
              onClick={() => openImageModal("package", category, subCategory, pkg)}
              title="Manage Image"
            >
              <FaImage />
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              className="me-1"
              onClick={() => openPackageModal(category, subCategory, pkg)}
            >
              <FaEdit />
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => handleDeletePackage(category, subCategory, pkg)}
              disabled={actionLoading}
            >
              <FaTrash />
            </Button>
          </div>
        </div>
      );
    }

    // Tiered package rendering
    return (
      <div className="border rounded p-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center">
            <ImageDisplay imageUrl={pkg.imageUrl} alt={pkg.name} size="35px" />
            <div className="ms-2">
              <strong>{pkg.name}</strong>
              <Badge bg="info" className="ms-2">
                <FaLayerGroup /> {pkg.tiers.length} Tiers
              </Badge>
              <br />
              <small className="text-muted">
                Price Range: {getPackageDisplayPrice(pkg)}
              </small>
              {pkg.isActive === false && (
                <Badge bg="secondary" className="ms-2">Inactive</Badge>
              )}
            </div>
          </div>
          <div>
            <Button
              variant="outline-success"
              size="sm"
              className="me-1"
              onClick={() => openTierModal(category, subCategory, pkg)}
            >
              <FaPlus /> Tier
            </Button>
            <Button
              variant="outline-info"
              size="sm"
              className="me-1"
              onClick={() => openImageModal("package", category, subCategory, pkg)}
              title="Manage Image"
            >
              <FaImage />
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              className="me-1"
              onClick={() => openPackageModal(category, subCategory, pkg)}
            >
              <FaEdit />
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => handleDeletePackage(category, subCategory, pkg)}
              disabled={actionLoading}
            >
              <FaTrash />
            </Button>
          </div>
        </div>

        {/* Tiers */}
        <div className="ms-3">
          {pkg.tiers.map((tier) => (
            <div key={tier.id} className="border-start ps-3 mb-2">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => toggleTierExpand(tier.id)}
                    className="me-2 p-0"
                  >
                    {expandedTiers[tier.id] ? <FaChevronDown /> : <FaChevronRight />}
                  </Button>
                  <FaTag className="text-warning me-1" />
                  <strong>{tier.name}</strong>
                  <Badge bg={tier.isActive !== false ? "success" : "secondary"} className="ms-2">
                    {tier.isActive !== false ? "Active" : "Inactive"}
                  </Badge>
                  <small className="text-muted ms-2">
                    {getTierDisplayPrice(tier)}
                  </small>
                  <small className="text-muted ms-2">
                    {tier.sizeVariants?.length || 0} variants
                  </small>
                </div>
                <div>
                  <Button
                    variant="outline-info"
                    size="sm"
                    className="me-1"
                    onClick={() => openImageModal("tier", category, subCategory, pkg, tier)}
                    title="Manage Image"
                  >
                    <FaImage />
                  </Button>
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="me-1"
                    onClick={() => openVariantModal(category, subCategory, pkg, tier)}
                  >
                    <FaPlus /> Variant
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="me-1"
                    onClick={() => openTierModal(category, subCategory, pkg, tier)}
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDeleteTier(category, subCategory, pkg, tier)}
                    disabled={actionLoading}
                  >
                    <FaTrash />
                  </Button>
                </div>
              </div>

              {/* Size Variants */}
              {expandedTiers[tier.id] && (
                <div className="ms-4 mt-2">
                  <Table size="sm" bordered className="mb-0">
                    <thead>
                      <tr>
                        <th>Variant</th>
                        <th>Duration</th>
                        <th>Price</th>
                        <th style={{ width: "120px" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(tier.sizeVariants || []).map((variant) => (
                        <tr key={variant.id || variant.type}>
                          <td><strong>{variant.type || variant.bhk}</strong></td>
                          <td>{variant.duration}</td>
                          <td>₹{variant.price}</td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-1"
                              onClick={() => openVariantModal(category, subCategory, pkg, tier, variant)}
                            >
                              <FaEdit size={12} />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteVariant(category, subCategory, pkg, tier, variant)}
                              disabled={actionLoading}
                            >
                              <FaTrash size={12} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {(tier.sizeVariants || []).length === 0 && (
                        <tr>
                          <td colSpan="4" className="text-center text-muted">
                            No variants. Click "Add Variant" to add.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading categories...</p>
        <p className="text-muted small">Connecting to: {apiUrl || "Loading..."}</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h2>Category Management</h2>
          <p className="text-muted">
            Manage categories, sub-categories, service packages, tiers, and size variants
          </p>
          <div className="small text-muted">
            <strong>🔗 API URL:</strong> {apiUrl}
            <Button
              variant="link"
              size="sm"
              className="ms-2 p-0"
              onClick={() => fetchCategories()}
              title="Refresh categories"
            >
              <FaImage /> Refresh
            </Button>
          </div>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={() => openCategoryModal()}>
            <FaPlus className="me-2" /> Add Category
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <strong>Error:</strong> {error}
          <hr />
          <small>
            API URL: {apiUrl}
            <br />
            Check the browser console for more details (F12 → Console tab)
          </small>
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Category Tree</h5>
              <small className="text-muted">
                {categories.length} categories loaded
              </small>
            </Card.Header>
            <Card.Body>
              {categories.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <p>No categories found.</p>
                  <p className="small">
                    Try checking if the API is running at: <strong>{apiUrl}</strong>
                  </p>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => fetchCategories()}
                  >
                    <FaImage className="me-1" /> Refresh
                  </Button>
                </div>
              ) : (
                categories.map((category) => (
                  <div key={category.id} className="mb-3 border rounded">
                    {/* Category Header */}
                    <div className="d-flex justify-content-between align-items-center p-3 bg-light">
                      <div className="d-flex align-items-center">
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => toggleCategoryExpand(category.id)}
                          className="me-2 p-0"
                        >
                          {expandedCategories[category.id] ? <FaChevronDown /> : <FaChevronRight />}
                        </Button>
                        <ImageDisplay imageUrl={category.imageUrl} alt={category.name} size="40px" />
                        <span className="fs-5 mx-2">{category.icon || "📁"}</span>
                        <span 
                          className="d-inline-block rounded-circle me-2 shadow-sm" 
                          style={{ width: '16px', height: '16px', backgroundColor: category.color || '#4f46e5', border: '1px solid rgba(0,0,0,0.15)', verticalAlign: 'middle' }} 
                          title={`Category Color: ${category.color || '#4f46e5'}`}
                        />
                        <strong>{category.name}</strong>
                        <Badge bg={category.isActive ? "success" : "secondary"} className="ms-2">
                          {category.isActive ? "Active" : "Inactive"}
                        </Badge>
                        <small className="text-muted ms-2">Order: {category.order || 0}</small>
                      </div>
                      <div>
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="me-1"
                          onClick={() => openImageModal("category", category)}
                          title="Manage Image"
                        >
                          <FaImage />
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-1"
                          onClick={() => handleToggleCategoryStatus(category)}
                          disabled={actionLoading}
                        >
                          {category.isActive ? <FaToggleOn /> : <FaToggleOff />}
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-1"
                          onClick={() => openCategoryModal(category)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteCategory(category)}
                          disabled={actionLoading}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </div>

                    {/* Sub-Categories */}
                    {expandedCategories[category.id] && (
                      <div className="p-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0">
                            <FaFolder className="me-2" />
                            Sub-Categories ({category.subCategories?.length || 0})
                          </h6>
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => openSubCategoryModal(category)}
                          >
                            <FaPlus className="me-1" /> Add Sub-Category
                          </Button>
                        </div>

                        {(category.subCategories || []).length === 0 ? (
                          <div className="text-muted text-center py-2">No sub-categories</div>
                        ) : (
                          (category.subCategories || []).map((sub) => (
                            <div key={sub.id} className="ms-4 mb-2 border-start ps-3">
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                  <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => toggleSubCategoryExpand(sub.id)}
                                    className="me-2 p-0"
                                  >
                                    {expandedSubCategories[sub.id] ? <FaChevronDown /> : <FaChevronRight />}
                                  </Button>
                                  <ImageDisplay imageUrl={sub.imageUrl} alt={sub.name} size="35px" />
                                  <FaTag className="mx-2 text-info" />
                                  <span>{sub.name}</span>
                                  <Badge bg={sub.isActive !== false ? "success" : "secondary"} className="ms-2">
                                    {sub.isActive !== false ? "Active" : "Inactive"}
                                  </Badge>
                                  <small className="text-muted ms-2">Order: {sub.order || 0}</small>
                                </div>
                                <div>
                                  <Button
                                    variant="outline-info"
                                    size="sm"
                                    className="me-1"
                                    onClick={() => openImageModal("subcategory", category, sub)}
                                    title="Manage Image"
                                  >
                                    <FaImage />
                                  </Button>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="me-1"
                                    onClick={() => openSubCategoryModal(category, sub)}
                                  >
                                    <FaEdit />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeleteSubCategory(category, sub)}
                                    disabled={actionLoading}
                                  >
                                    <FaTrash />
                                  </Button>
                                </div>
                              </div>

                              {/* Packages */}
                              {expandedSubCategories[sub.id] && (
                                <div className="mt-2 ms-4">
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <small className="text-muted">
                                      <FaBox className="me-1" /> Packages / Variants ({sub.packages?.length || 0})
                                    </small>
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      onClick={() => openPackageModal(category, sub)}
                                    >
                                      <FaPlus className="me-1" /> Add Package
                                    </Button>
                                  </div>
                                  {(sub.packages || []).length === 0 ? (
                                    <div className="text-muted small text-center py-1">No packages</div>
                                  ) : (
                                    <div>
                                      {(sub.packages || []).map((pkg) => (
                                        <div key={pkg.id} className="mb-2">
                                          {renderPackageWithTiers(category, sub, pkg)}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ── Category Modal ── */}
      <Modal show={showCategoryModal} onHide={() => setShowCategoryModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedCategory ? "Edit Category" : "Add Category"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    placeholder="e.g., Plumbing, Electrical, Cleaning"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Icon (emoji)</Form.Label>
                  <Form.Control
                    type="text"
                    value={categoryForm.icon}
                    onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                    placeholder="🔧"
                    maxLength={2}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Display Order</Form.Label>
                  <Form.Control
                    type="number"
                    value={categoryForm.order}
                    onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 0 })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Discount Percentage (%)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="100"
                    value={categoryForm.discountPercentage || 0}
                    onChange={(e) => setCategoryForm({ ...categoryForm, discountPercentage: parseFloat(e.target.value) || 0 })}
                    placeholder="e.g. 10"
                  />
                  <Form.Text className="text-muted">
                    Applies a percentage discount to all services in this category.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Category Theme Color</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <Form.Control
                  type="color"
                  value={categoryForm.color || "#4f46e5"}
                  onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                  style={{ width: '50px', height: '38px', padding: '2px', cursor: 'pointer' }}
                />
                <Form.Control
                  type="text"
                  value={categoryForm.color || "#4f46e5"}
                  onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                  placeholder="#4f46e5"
                  style={{ fontFamily: 'monospace', maxWidth: '140px' }}
                />
                <span className="text-muted small ms-2">Select category accent color</span>
              </div>
              <div className="mt-2 d-flex gap-2 flex-wrap align-items-center">
                <small className="text-muted me-1">Presets:</small>
                {["#4F46E5", "#2563EB", "#059669", "#D97706", "#DC2626", "#7C3AED", "#DB2777", "#0284C7", "#10B981", "#8B5CF6"].map((c) => (
                  <span
                    key={c}
                    onClick={() => setCategoryForm({ ...categoryForm, color: c })}
                    style={{
                      width: '24px',
                      height: '24px',
                      backgroundColor: c,
                      borderRadius: '50%',
                      cursor: 'pointer',
                      boxShadow: categoryForm.color?.toUpperCase() === c ? '0 0 0 2px #fff, 0 0 0 4px ' + c : 'none',
                      border: '1px solid rgba(0,0,0,0.15)'
                    }}
                    title={c}
                  />
                ))}
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Image URL</Form.Label>
              <Form.Control
                type="text"
                value={categoryForm.imageUrl}
                onChange={(e) => setCategoryForm({ ...categoryForm, imageUrl: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
              <Form.Text className="text-muted">
                You can also upload images using the image management button in the category tree
              </Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Check
                type="switch"
                label="Active"
                checked={categoryForm.isActive}
                onChange={(e) => setCategoryForm({ ...categoryForm, isActive: e.target.checked })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCategoryModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={selectedCategory ? handleUpdateCategory : handleCreateCategory}
            disabled={actionLoading}
          >
            {actionLoading ? <Spinner size="sm" /> : selectedCategory ? "Update" : "Create"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Sub-Category Modal ── */}
      <Modal show={showSubCategoryModal} onHide={() => setShowSubCategoryModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedSubCategory ? "Edit Sub-Category" : `Add Sub-Category to "${selectedCategory?.name}"`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={subCategoryForm.name}
                    onChange={(e) => setSubCategoryForm({ ...subCategoryForm, name: e.target.value })}
                    placeholder="e.g., Pipe Repair, Leak Fixing"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Display Order</Form.Label>
                  <Form.Control
                    type="number"
                    value={subCategoryForm.order}
                    onChange={(e) => setSubCategoryForm({ ...subCategoryForm, order: parseInt(e.target.value) || 0 })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Image URL</Form.Label>
              <Form.Control
                type="text"
                value={subCategoryForm.imageUrl}
                onChange={(e) => setSubCategoryForm({ ...subCategoryForm, imageUrl: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
            </Form.Group>
            <Form.Group>
              <Form.Check
                type="switch"
                label="Active"
                checked={subCategoryForm.isActive}
                onChange={(e) => setSubCategoryForm({ ...subCategoryForm, isActive: e.target.checked })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSubCategoryModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={selectedSubCategory ? handleUpdateSubCategory : handleCreateSubCategory}
            disabled={actionLoading}
          >
            {actionLoading ? <Spinner size="sm" /> : selectedSubCategory ? "Update" : "Create"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Package Modal ── */}
      <Modal show={showPackageModal} onHide={() => setShowPackageModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedPackage ? "Edit Package" : `Add Package to "${selectedSubCategory?.name}"`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Package Name *</Form.Label>
              <Form.Control
                type="text"
                value={packageForm.name}
                onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                placeholder="e.g., Standard Service, Premium Package"
              />
              <Form.Text className="text-muted">
                Name of the service variant/package (e.g., Basic, Standard, Premium)
              </Form.Text>
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Price (₹) *</Form.Label>
                  <Form.Control
                    type="number"
                    value={packageForm.price}
                    onChange={(e) => setPackageForm({ ...packageForm, price: e.target.value })}
                    placeholder="0"
                    step="0.01"
                    min="0"
                  />
                  <Form.Text className="text-muted">
                    For tiered packages, you can set different prices per tier/variant
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Unit</Form.Label>
                  <Form.Select
                    value={packageForm.unit}
                    onChange={(e) => setPackageForm({ ...packageForm, unit: e.target.value })}
                  >
                    <option value="per visit">Per Visit</option>
                    <option value="per hour">Per Hour</option>
                    <option value="per day">Per Day</option>
                    <option value="per month">Per Month</option>
                    <option value="fixed">Fixed Price</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Image URL</Form.Label>
              <Form.Control
                type="text"
                value={packageForm.imageUrl}
                onChange={(e) => setPackageForm({ ...packageForm, imageUrl: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
            </Form.Group>
            <Form.Group>
              <Form.Check
                type="switch"
                label="Active"
                checked={packageForm.isActive}
                onChange={(e) => setPackageForm({ ...packageForm, isActive: e.target.checked })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPackageModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={selectedPackage ? handleUpdatePackage : handleCreatePackage}
            disabled={actionLoading}
          >
            {actionLoading ? <Spinner size="sm" /> : selectedPackage ? "Update" : "Create"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Tier Modal (with full variant management) ── */}
      <Modal show={showTierModal} onHide={() => setShowTierModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedTier ? `Edit Tier: ${selectedTier.name}` : `Add Tier to "${selectedPackage?.name}"`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Tier Name *</Form.Label>
              <Form.Control
                type="text"
                value={tierForm.name}
                onChange={(e) => setTierForm({ ...tierForm, name: e.target.value })}
                placeholder="e.g., Regular, Premium, Essential"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                label="Active"
                checked={tierForm.isActive}
                onChange={(e) => setTierForm({ ...tierForm, isActive: e.target.checked })}
              />
            </Form.Group>

            <hr />
            <h6 className="mb-3">
              <FaList className="me-2" /> Size Variants
              <Button
                variant="outline-success"
                size="sm"
                className="ms-2"
                onClick={addVariantToTier}
              >
                <FaPlus /> Add Variant
              </Button>
            </h6>

            {tierForm.variants.length === 0 && (
              <div className="text-muted text-center py-2">
                No variants added. Click "Add Variant" to add size options.
              </div>
            )}

            {tierForm.variants.map((variant, index) => (
              <div key={index} className="border rounded p-3 mb-2">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>Variant #{index + 1}</strong>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeVariantFromTier(index)}
                    disabled={tierForm.variants.length === 1}
                  >
                    <FaTrash />
                  </Button>
                </div>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-2">
                      <Form.Label>Type / BHK</Form.Label>
                      <Form.Control
                        type="text"
                        value={variant.type || variant.bhk || ""}
                        onChange={(e) => updateVariantInTier(index, "type", e.target.value)}
                        placeholder="e.g., 1 BHK"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-2">
                      <Form.Label>Duration</Form.Label>
                      <Form.Control
                        type="text"
                        value={variant.duration}
                        onChange={(e) => updateVariantInTier(index, "duration", e.target.value)}
                        placeholder="e.g., 1 hr"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-2">
                      <Form.Label>Price (₹)</Form.Label>
                      <Form.Control
                        type="number"
                        value={variant.price}
                        onChange={(e) => updateVariantInTier(index, "price", e.target.value)}
                        placeholder="0"
                        step="0.01"
                        min="0"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            ))}

            <small className="text-muted">
              <FaList className="me-1" /> Add different size variants with specific pricing (e.g., 1 BHK: ₹3221, 2 BHK: ₹3694)
            </small>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTierModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={selectedTier ? handleUpdateTier : handleCreateTier}
            disabled={actionLoading}
          >
            {actionLoading ? <Spinner size="sm" /> : selectedTier ? "Update Tier" : "Create Tier"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Size Variant Modal (individual variant) ── */}
      <Modal show={showSizeVariantModal} onHide={() => setShowSizeVariantModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedVariant ? "Edit Size Variant" : `Add Size Variant to "${selectedTier?.name}"`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Variant Type *</Form.Label>
                  <Form.Control
                    type="text"
                    value={variantForm.type}
                    onChange={(e) => setVariantForm({ ...variantForm, type: e.target.value })}
                    placeholder="e.g., 1 BHK, 2 BHK, Furnished, Standard"
                  />
                  <Form.Text className="text-muted">
                    e.g., 1 BHK, 2 BHK, 3 BHK, Furnished, Unfurnished
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>BHK (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={variantForm.bhk}
                    onChange={(e) => setVariantForm({ ...variantForm, bhk: e.target.value })}
                    placeholder="e.g., 1 BHK, 2 BHK"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Duration *</Form.Label>
                  <Form.Control
                    type="text"
                    value={variantForm.duration}
                    onChange={(e) => setVariantForm({ ...variantForm, duration: e.target.value })}
                    placeholder="e.g., 1 hr, 30 mins, 2 hrs"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Price (₹) *</Form.Label>
                  <Form.Control
                    type="number"
                    value={variantForm.price}
                    onChange={(e) => setVariantForm({ ...variantForm, price: e.target.value })}
                    placeholder="0"
                    step="0.01"
                    min="0"
                  />
                </Form.Group>
              </Col>
            </Row>
            <small className="text-muted">
              <FaList className="me-1" /> Add specific pricing for different property sizes or configurations.
            </small>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSizeVariantModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={selectedVariant ? handleUpdateVariant : handleCreateVariant}
            disabled={actionLoading}
          >
            {actionLoading ? <Spinner size="sm" /> : selectedVariant ? "Update" : "Create"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Image Management Modal ── */}
      <Modal show={showImageModal} onHide={() => setShowImageModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Manage Image: <strong>{imageEntity.name}</strong>
            <Badge bg="info" className="ms-2">
              {imageEntity.type === "category"
                ? "Category"
                : imageEntity.type === "subcategory"
                ? "Sub-Category"
                : imageEntity.type === "tier"
                ? "Tier"
                : "Package"}
            </Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {imageEntity.currentImageUrl && (
            <div className="mb-4">
              <h6>Current Image</h6>
              <img
                src={imageEntity.currentImageUrl}
                alt={imageEntity.name}
                style={{
                  maxWidth: "100%",
                  maxHeight: "200px",
                  objectFit: "contain",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Upload New Image</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={handleImageFileChange}
              disabled={imageUploading}
            />
            <Form.Text className="text-muted">
              Supported formats: JPG, PNG, GIF, WebP. Max size: 5MB.
            </Form.Text>
          </Form.Group>

          {imagePreview && (
            <div className="mb-3">
              <h6>Preview</h6>
              <img
                src={imagePreview}
                alt="Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "200px",
                  objectFit: "contain",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                }}
              />
            </div>
          )}

          {imageFile && (
            <div className="mt-2">
              <Badge bg="info">Selected: {imageFile.name}</Badge>
              <Button
                variant="outline-danger"
                size="sm"
                className="ms-2"
                onClick={() => {
                  setImageFile(null);
                  setImagePreview(null);
                }}
              >
                <FaTimesCircle /> Clear
              </Button>
            </div>  
          )}
        </Modal.Body>
        <Modal.Footer>
          {imageEntity.currentImageUrl && (
            <Button
              variant="danger"
              onClick={handleImageDelete}
              disabled={imageUploading}
              className="me-auto"
            >
              {imageUploading ? <Spinner size="sm" /> : "Remove Image"}
            </Button>
          )}
          <Button variant="secondary" onClick={() => setShowImageModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleImageUpload}
            disabled={!imageFile || imageUploading}
          >
            {imageUploading ? (
              <>
                <Spinner size="sm" className="me-2" /> Uploading...
              </>
            ) : (
              <>
                <FaUpload className="me-2" /> Upload Image
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminCategoryManagement;